import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router";
import { useMe, useStatusBar } from "@/api/queries";
import { useTheme } from "@/app/theme-context";
import { Glyph } from "@/components/ui/Glyph";
import { clock } from "@/lib/format";
import { MORE_NAV, PHONE_TABS, isCurrent, type Destination } from "./nav";
import { useShell } from "./shell-context";
import { useModal } from "./useModal";
import styles from "./tabbar.module.css";

/** Three dots in a row: the More tab. */
function MoreIcon() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <circle cx="5" cy="12" r="1.9" />
      <circle cx="12" cy="12" r="1.9" />
      <circle cx="19" cy="12" r="1.9" />
    </svg>
  );
}

function Tab({ item, current, badge }: { item: Destination; current: boolean; badge?: number }) {
  return (
    <Link
      to={item.to}
      className={[styles.tab, item.needs ? styles.needs : ""].filter(Boolean).join(" ")}
      aria-current={current ? "page" : undefined}
    >
      <span className={styles.icon}>
        <Glyph name={item.glyph} size={21} strokeWidth={1.9} />
        {badge ? (
          <span className={styles.badge}>
            {badge}
            <span className="visually-hidden"> waiting for you</span>
          </span>
        ) : null}
      </span>
      <span className={styles.label}>{item.label}</span>
    </Link>
  );
}

/** Everything that isn't a tab, plus search, the theme and the keyboard shortcuts. */
function MoreSheet({ onClose }: { onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  useModal(box, onClose, close);
  const { pathname } = useLocation();
  const { theme, toggle } = useTheme();
  const shell = useShell();
  const status = useStatusBar();
  const data = status.data;
  const lagging = data?.sources.filter((s) => s.status !== "ok") ?? [];

  return createPortal(
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby="zh-more-title"
        className={styles.sheet}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className={styles.grab} aria-hidden="true" />
        <div className={styles.sheetHead}>
          <h2 id="zh-more-title" className={styles.sheetTitle}>
            More
          </h2>
          <button
            ref={close}
            type="button"
            className={styles.close}
            aria-label="Close"
            onClick={onClose}
          >
            <Glyph name="x" size={18} strokeWidth={2.2} />
          </button>
        </div>
        <nav aria-label="More places">
          <ul className={styles.grid}>
            {MORE_NAV.map((item) => {
              const current = isCurrent(item, pathname);
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={styles.place}
                    aria-current={current ? "page" : undefined}
                    onClick={onClose}
                  >
                    <span className={styles.placeIcon}>
                      <Glyph name={item.glyph} size={21} strokeWidth={1.9} />
                    </span>
                    <span className={styles.placeLabel}>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className={styles.actions}>
          {shell.available ? (
            <button
              type="button"
              className={styles.action}
              onClick={() => {
                onClose();
                shell.openPalette();
              }}
            >
              <Glyph name="search" size={18} />
              Search cases, flows and commands
            </button>
          ) : null}
          <button type="button" className={styles.action} onClick={toggle}>
            <Glyph name={theme === "light" ? "moon" : "sun"} size={18} strokeWidth={1.9} />
            {theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
          </button>
          {shell.available ? (
            <button
              type="button"
              className={`${styles.action} ${styles.keysOnly}`}
              onClick={() => {
                onClose();
                shell.openShortcuts();
              }}
            >
              <Glyph name="lines" size={18} />
              Keyboard shortcuts
            </button>
          ) : null}
        </div>
        {data ? (
          <p className={styles.status}>
            {lagging.map((s) => (
              <span key={s.key} className={styles.statusWarn}>
                <span className={styles.statusDot} aria-hidden="true" />
                {s.name}, {s.status_text}
              </span>
            ))}
            <span>
              {data.audit.ok
                ? `Audit chain verified ${clock(data.audit.verified_at)}`
                : `Audit chain broken at entry ${String(data.audit.broken_at_seq)}`}
            </span>
            <span>ZeaOps {data.version}</span>
          </p>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/**
 * On a phone the rail becomes a bar of tabs along the bottom, where the thumb is: Home, Needs
 * you with its count, Cases, Flows, and More for everything else.
 */
export function TabBar() {
  const { pathname } = useLocation();
  const me = useMe();
  const waiting = me.data?.needs_you_count ?? 0;
  const [more, setMore] = useState(false);
  const inMore = MORE_NAV.find((d) => isCurrent(d, pathname));

  return (
    <>
      <nav aria-label="Main" className={styles.bar}>
        {PHONE_TABS.map((item) => (
          <Tab
            key={item.to}
            item={item}
            current={isCurrent(item, pathname)}
            badge={item.needs ? waiting : undefined}
          />
        ))}
        <button
          type="button"
          className={styles.tab}
          aria-haspopup="dialog"
          aria-expanded={more}
          data-current={inMore ? "true" : undefined}
          onClick={() => setMore(true)}
        >
          <span className={styles.icon}>
            <MoreIcon />
          </span>
          <span className={styles.label}>
            More
            {inMore ? <span className="visually-hidden">, you are on {inMore.label}</span> : null}
          </span>
        </button>
      </nav>
      {more ? <MoreSheet onClose={() => setMore(false)} /> : null}
    </>
  );
}
